import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import type { PlotType } from '@edge/charts-indicators'
import { useChartColorPicker } from './color-picker-context'
import { normalizeHex } from '../utils/color'
import { composeColorOpacity, decomposeColorOpacity, extractHex } from './drawerControls-utils'
import { Z_MODAL_POPPER } from './zLayers'

/* ------------------------------------------------------------------ */
/*  Shared drawer chrome + form controls                               */
/*                                                                     */
/*  Extracted verbatim from IndicatorSettingsDrawer so the overlay     */
/*  settings drawer can reuse the same color / line-width / line-style */
/*  rows without duplicating them. Behaviour is unchanged — the        */
/*  indicator drawer now imports these instead of defining locals.     */
/*                                                                     */
/*  Also the ONE home for the settings-modal controls (SwatchInput /   */
/*  PickerSwatch / ThemedColorRow / NumberRow) — SettingsModal grew    */
/*  hand-rolled copies of the same row scaffold + picker-with-native-  */
/*  fallback logic before this module existed. Non-component helpers   */
/*  (class-string builders, color/opacity codecs) live in              */
/*  drawerControls-utils.ts so this file exports only components       */
/*  (react-refresh).                                                   */
/* ------------------------------------------------------------------ */

export const SECONDARY_BUTTON_CLS =
  'h-7 rounded-sm border border-border-strong bg-accent px-3 text-xs font-medium text-[hsl(var(--chart-text))] transition-colors hover:bg-background-hover'

/** One box for every drawer input control — number inputs and selects share
 *  it so the Inputs and Style tabs line up in a single column. */
export const INPUT_BOX_CLS =
  'h-7 w-24 rounded border border-border-strong bg-field px-2 text-xs text-[hsl(var(--chart-text))] outline-hidden focus:border-primary'

/** Same box, w-32 — for selects whose option labels don't fit the w-24 default. */
export const INPUT_BOX_WIDE_CLS =
  'h-7 w-32 rounded border border-border-strong bg-field px-2 text-xs text-[hsl(var(--chart-text))] outline-hidden focus:border-primary'

export interface DrawerSelectOption {
  value: string
  label: string
}

/**
 * App-styled select — replaces the native <select>, whose popup is the OS menu
 * and ignores the app's design language entirely. Trigger wears the shared
 * input box; the menu is the Toolbar dropdown chrome (bg-popover, accent
 * hover), portaled to <body> so the drawers' overflow containers cannot clip
 * it (usePortalDismiss guards it via data-chart-select-menu).
 */
export function DrawerSelect({
  value,
  options,
  onChange,
  ariaLabel,
  buttonClassName,
}: {
  value: string
  options: ReadonlyArray<DrawerSelectOption>
  onChange: (v: string) => void
  ariaLabel?: string
  /** Overrides the trigger box (defaults to INPUT_BOX_CLS). */
  buttonClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const [rect, setRect] = useState<{
    left: number
    top: number
    width: number
    up: boolean
  } | null>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  const selected = options.find((o) => o.value === value)

  const toggle = () => {
    if (open) {
      setOpen(false)
      return
    }
    const r = btnRef.current?.getBoundingClientRect()
    if (!r) return
    // Flip upward when the menu would run off the bottom of the viewport.
    const up = r.bottom + Math.min(options.length * 26 + 10, 256) > window.innerHeight
    setRect({ left: r.left, top: up ? r.top - 4 : r.bottom + 4, width: r.width, up })
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (target?.closest?.('[data-chart-select-menu]')) return
      if (btnRef.current?.contains(target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [open])

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        className={
          (buttonClassName ?? INPUT_BOX_CLS) +
          ' flex cursor-pointer items-center justify-between gap-1 text-left' +
          (open ? ' border-primary' : '')
        }
      >
        <span className="truncate">{selected?.label ?? value}</span>
        <svg
          width={10}
          height={10}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="shrink-0 opacity-60"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            data-chart-select-menu=""
            data-chart-popup=""
            role="listbox"
            style={{
              position: 'fixed',
              left: rect.left,
              top: rect.top,
              minWidth: rect.width,
              zIndex: Z_MODAL_POPPER,
              transform: rect.up ? 'translateY(-100%)' : undefined,
            }}
            className="max-h-64 overflow-y-auto rounded-md border border-primary/15 bg-popover p-1 shadow-lg shadow-black/30"
          >
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                onClick={() => {
                  onChange(o.value)
                  setOpen(false)
                }}
                // State hierarchy, strongest to weakest: SELECTED (primary
                // fill + checkmark) > hover (subtle accent) > rest. The old
                // scheme inverted this — full-accent hover outshone a
                // half-accent selection with no glyph, so whatever the cursor
                // touched read as "active".
                className={
                  'flex w-full items-center justify-between gap-2 rounded-sm px-2.5 py-1 text-left text-xs transition-colors ' +
                  (o.value === value
                    ? 'bg-primary/15 text-[hsl(var(--chart-text))]'
                    : 'text-[hsl(var(--chart-text)/0.75)] hover:bg-accent/40 hover:text-[hsl(var(--chart-text))]')
                }
              >
                <span className="truncate">{o.label}</span>
                {o.value === value && (
                  <svg
                    width={12}
                    height={12}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                    className="shrink-0 text-primary"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}

export const SMALL_SELECT_CLS =
  'h-6 rounded border border-border-strong bg-field px-1.5 text-[11px] text-[hsl(var(--chart-text))] outline-hidden cursor-pointer'

/**
 * Row scaffold shared by every drawer/modal settings row: muted label on the
 * left, the control(s) on the right. This exact div+span pair used to repeat
 * ~15× across the settings modal and both drawers.
 */
export function SettingRow({
  label,
  badge,
  children,
}: {
  label: string
  /** Optional chip after the label (e.g. a Beta tag). Kept out of `label` so
   *  aria-labels and text queries still see the plain string. */
  badge?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="flex min-h-6 items-center justify-between">
      <span className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
        <span>{label}</span>
        {badge}
      </span>
      {children}
    </div>
  )
}

/** A thin labelled divider grouping stacked sections in a single-scroll drawer —
 *  used since the Inputs/Style and Style/Coordinates tab splits were merged into
 *  one view. Shared so both drawers' section headers read identically. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 mb-2 border-t border-border-strong pt-3 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
      {children}
    </div>
  )
}

/** Native color swatch fallback used when no host picker is injected.
 *  `title` is optional: rows that already label the swatch elsewhere pass
 *  nothing and the attribute is simply omitted. */
export function SwatchInput({
  value,
  onChange,
  title,
}: {
  value: string
  onChange: (v: string) => void
  title?: string
}) {
  return (
    <input
      type="color"
      title={title}
      value={extractHex(value)}
      onChange={(e) => onChange(e.target.value)}
      className="h-5 w-5 cursor-pointer rounded ring-1 ring-inset ring-[hsl(var(--chart-text)/0.2)] bg-transparent p-0"
    />
  )
}

/** Injected-picker swatch with the native fallback — ONE component so every
 *  color control in the modal (TPO rows included) opens the same picker. */
export function PickerSwatch({
  value,
  onChange,
  title,
  showOpacity = true,
}: {
  value: string
  onChange: (v: string) => void
  title: string
  showOpacity?: boolean
}) {
  const Picker = useChartColorPicker()
  if (Picker) return <>{Picker({ value, onChange, title, showOpacity })}</>
  return <SwatchInput value={value} onChange={onChange} title={title} />
}

/**
 * "Following the theme" affordance for a color row.
 *
 * Rendered only when the row is overriding the app theme, so its absence means
 * "this swatch is the theme's color". Clicking hands the row back to the theme.
 * Deliberately NOT a disabled/greyed swatch when following: a greyed control
 * reads as broken, and the swatch still has a real, meaningful color to show.
 */
export function FollowThemeReset({ onReset, label }: { onReset: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onReset}
      title={`Reset ${label.toLowerCase()} to the theme color`}
      aria-label={`Reset ${label} to theme color`}
      className="flex h-4 w-4 items-center justify-center rounded-sm text-[hsl(var(--muted-foreground))] transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))]"
    >
      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor">
        <path
          d="M3 12a9 9 0 1 0 3-6.7L3 8"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M3 3v5h5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  )
}

/**
 * Color + opacity row. Opacity lives inside the color picker popover (no
 * separate slider). The picker emits either a hex string (opacity = 100%) or
 * an `rgba(...)` string, which is split back into hex color + 0–1 opacity so
 * the data model stays unchanged (the indicator figure-style shape).
 *
 * Sibling `ThemedColorRow` below is the settings-modal flavor: it passes the
 * raw color STRING through unchanged and adds the follow-theme reset. The two
 * are kept separate because they speak different data models, not because the
 * rows look different.
 */
export function ColorRow({
  label,
  color,
  opacity,
  onChange,
}: {
  label: string
  color: string
  opacity: number
  onChange: (patch: { color: string; opacity: number }) => void
}) {
  const Picker = useChartColorPicker()
  const hex = normalizeHex(color)
  const composed = composeColorOpacity(hex, opacity)
  return (
    <SettingRow label={label}>
      {Picker ? (
        Picker({
          value: composed,
          onChange: (next) => {
            const decomposed = decomposeColorOpacity(next)
            onChange({ color: decomposed.hex, opacity: decomposed.alpha })
          },
          title: label,
          showOpacity: true,
        })
      ) : (
        // Native <input type="color"> can't express alpha — fall back to
        // emitting just the color change with the existing opacity.
        <SwatchInput value={hex} onChange={(v) => onChange({ color: v, opacity })} />
      )}
    </SettingRow>
  )
}

/**
 * Settings-modal color row: raw string value (hex or rgba) in, raw string out
 * — the ChartSettings data model. `onReset` present ⇒ this row currently
 * overrides the app theme; renders the follow-theme reset arrow beside the
 * swatch. See `ColorRow` above for the split color+opacity flavor.
 */
export function ThemedColorRow({
  label,
  value,
  onChange,
  showOpacity = true,
  onReset,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  showOpacity?: boolean
  /** Present ⇒ this row currently overrides the theme; renders the reset arrow. */
  onReset?: () => void
}) {
  return (
    <SettingRow label={label}>
      <div className="flex items-center gap-1.5">
        {onReset && <FollowThemeReset onReset={onReset} label={label} />}
        <PickerSwatch value={value} onChange={onChange} title={label} showOpacity={showOpacity} />
      </div>
    </SettingRow>
  )
}

/**
 * Bare numeric input shared by the settings modal, the indicator drawer's
 * param controls, and the overlay drawer's coordinate editor. Parameterized
 * where the old copies genuinely differed (box classes, step grammar, int
 * rounding, fallback render value); the parse guard is common to all three:
 * ignore empty/non-finite input rather than emitting NaN.
 */
export function DrawerNumberInput({
  value,
  onChange,
  min,
  max,
  step,
  ariaLabel,
  className = INPUT_BOX_CLS,
  integer = false,
  fallback,
}: {
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  /** `'any'` disables browser step validation (full-precision price fields). */
  step?: number | 'any'
  ariaLabel?: string
  /** Overrides the input box (defaults to INPUT_BOX_CLS). */
  className?: string
  /** Round to the nearest integer before emitting (int param schemas). */
  integer?: boolean
  /** Rendered when `value` is not finite (e.g. a schema default). */
  fallback?: number
}) {
  // Local draft so the field can go EMPTY (or hold an intermediate like "1.")
  // mid-edit instead of snapping straight back to the committed number — the
  // user can clear it entirely and type a fresh value. null = not editing →
  // show the committed value; blur re-syncs (an empty/invalid draft is dropped,
  // reverting to the last committed value rather than persisting a blank).
  const [draft, setDraft] = useState<string | null>(null)
  const committed = Number.isFinite(value)
    ? String(value)
    : fallback != null
      ? String(fallback)
      : ''
  return (
    <input
      type="number"
      aria-label={ariaLabel}
      value={draft ?? committed}
      min={min}
      max={max}
      step={step}
      onChange={(e) => {
        const raw = e.target.value
        setDraft(raw)
        if (raw === '') return
        const n = Number(raw)
        if (!Number.isFinite(n)) return
        onChange(integer ? Math.round(n) : n)
      }}
      onBlur={() => setDraft(null)}
      className={className}
    />
  )
}

/**
 * Settings-modal number row: compact right-aligned box, plus optional one-tap
 * preset chips underneath (used by margins / fixed-grid increments).
 */
export function NumberRow({
  label,
  value,
  onChange,
  min,
  step,
  presets,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min?: number
  step?: number
  presets?: number[]
}) {
  return (
    <div className="flex flex-col gap-1">
      <SettingRow label={label}>
        <DrawerNumberInput
          value={value}
          min={min ?? 0}
          step={step ?? 0.01}
          onChange={onChange}
          className="h-6 w-16 rounded border border-border-strong bg-field px-1.5 text-right text-[11px] text-[hsl(var(--chart-text))] outline-hidden focus:border-primary"
        />
      </SettingRow>
      {presets && (
        <div className="flex justify-end gap-1">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              className={
                'rounded-sm border px-2 py-px text-[10px] ' +
                (value === p
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border-strong bg-background text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--chart-text))]')
              }
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function LineWidthRow({
  value,
  onChange,
}: {
  value: number
  onChange: (v: number) => void
}) {
  return (
    <SettingRow label="Line width">
      <DrawerSelect
        ariaLabel="Line width"
        value={String(value)}
        options={[1, 2, 3, 4, 5].map((w) => ({ value: String(w), label: `${w}px` }))}
        onChange={(v) => onChange(Number(v))}
      />
    </SettingRow>
  )
}

const LINE_STYLE_LABELS: Record<'solid' | 'dashed' | 'dotted', string> = {
  solid: 'Solid',
  dashed: 'Dashed',
  dotted: 'Dotted',
}

export function LineStyleRow({
  value,
  onChange,
  options = ['solid', 'dashed', 'dotted'],
}: {
  value: 'solid' | 'dashed' | 'dotted'
  onChange: (v: 'solid' | 'dashed' | 'dotted') => void
  /** Which line styles to offer. Overlays only model solid/dashed. */
  options?: Array<'solid' | 'dashed' | 'dotted'>
}) {
  return (
    <SettingRow label="Line style">
      <DrawerSelect
        ariaLabel="Line style"
        value={value}
        options={options.map((opt) => ({ value: opt, label: LINE_STYLE_LABELS[opt] }))}
        onChange={(v) => onChange(v as 'solid' | 'dashed' | 'dotted')}
      />
    </SettingRow>
  )
}

const PLOT_TYPE_LABELS: Record<PlotType, string> = {
  line: 'Line',
  histogram: 'Histogram',
  columns: 'Columns',
  circles: 'Circles',
}

export function PlotTypeRow({
  value,
  onChange,
}: {
  value: PlotType
  onChange: (v: PlotType) => void
}) {
  return (
    <SettingRow label="Plot type">
      <DrawerSelect
        ariaLabel="Plot type"
        value={value}
        options={(Object.keys(PLOT_TYPE_LABELS) as PlotType[]).map((t) => ({
          value: t,
          label: PLOT_TYPE_LABELS[t],
        }))}
        onChange={(v) => onChange(v as PlotType)}
      />
    </SettingRow>
  )
}

/** A rendered line preview at the given width + dash style (+ optional opacity)
 *  — the visual sample the consolidated line-style control shows in its trigger
 *  and its weight/style option buttons. */
export function LineSample({
  size,
  style,
  opacity = 1,
  width = 40,
}: {
  size: number
  style: 'solid' | 'dashed' | 'dotted'
  opacity?: number
  width?: number
}) {
  const dash = style === 'dashed' ? '5 4' : style === 'dotted' ? '1.5 3.5' : undefined
  return (
    <svg
      width={width}
      height={12}
      viewBox={`0 0 ${width} 12`}
      aria-hidden
      className="shrink-0"
      style={{ opacity }}
    >
      <line
        x1={1}
        y1={6}
        x2={width - 1}
        y2={6}
        stroke="currentColor"
        strokeWidth={size}
        strokeDasharray={dash}
        strokeLinecap={style === 'dotted' ? 'round' : 'butt'}
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/*  Shared drawer chrome (header / toggle)                              */
/*  (drawerTabCls and the other class-string builders live in           */
/*  drawerControls-utils.ts — react-refresh.)                           */
/* ------------------------------------------------------------------ */

/** Drawer/modal header: title (+ optional subtitle) and an Esc close button. */
export function DrawerHeader({
  label,
  subtitle,
  onClose,
  closeAriaLabel = 'Close',
  onPointerDown,
}: {
  label: string
  subtitle?: string
  onClose: () => void
  closeAriaLabel?: string
  /** Drag-handle passthrough — wire to `useDraggablePanel`'s `onHeaderPointerDown`
   *  so the header can drag the panel it belongs to. Optional: headers that
   *  aren't draggable (none, currently) simply omit it. */
  onPointerDown?: (e: React.PointerEvent) => void
}) {
  return (
    <div
      onPointerDown={onPointerDown}
      className={
        'flex shrink-0 items-start justify-between border-b border-border-strong px-4 py-3' +
        // Grab cursor only when the header is actually a drag handle, matching
        // the Settings modal's header (cursor-grab → grabbing while pressed).
        (onPointerDown ? ' cursor-grab touch-none select-none active:cursor-grabbing' : '')
      }
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-[hsl(var(--chart-text))]">{label}</div>
        {subtitle && (
          <div className="mt-0.5 text-[11px] leading-snug text-[hsl(var(--muted-foreground))]">
            {subtitle}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        title="Close (Esc)"
        aria-label={closeAriaLabel}
        className="ml-3 shrink-0 rounded border border-border-strong px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--muted-foreground))] transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))]"
      >
        Esc
      </button>
    </div>
  )
}

/** Pill switch shared by the settings modal (sm) + drawer toggles (md). Matches
 *  the app's shadcn Switch geometry so chart toggles read identically to the
 *  rest of the platform: the thumb is flex-centered (`items-center`) rather than
 *  absolutely positioned, so it never rides low against the track border, and it
 *  slides via `translate-x-[calc(100%-2px)]` (correct travel at both sizes). The
 *  thumb stays chart-chrome white and the track uses chart CSS vars so the
 *  component remains standalone (no shadcn/app dependency). */
export function Toggle({
  checked,
  onChange,
  size = 'md',
  ariaLabel,
  disabled,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  size?: 'sm' | 'md'
  ariaLabel?: string
  disabled?: boolean
}) {
  const track = size === 'sm' ? 'h-3.5 w-6' : 'h-[1.15rem] w-8'
  const thumb = size === 'sm' ? 'size-3' : 'size-4'
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={
        `inline-flex shrink-0 items-center rounded-full border border-transparent p-0 transition-colors ${track} ` +
        // Off pairs a chart-var fill with the transparent border — on dark
        // custom themes `bg-muted` can equal the panel background, leaving an
        // invisible toggle. The border rides both states so the track never
        // changes size.
        (checked ? 'bg-primary' : 'bg-[hsl(var(--chart-text)/0.18)]') +
        (disabled ? ' cursor-not-allowed opacity-60' : '')
      }
    >
      <span
        className={
          `pointer-events-none block rounded-full bg-white transition-transform ${thumb} ` +
          (checked ? 'translate-x-[calc(100%-2px)]' : 'translate-x-0')
        }
      />
    </button>
  )
}
